/**
 * InteriorMapping — Van Dongen's building-window trick (Spider-Man /
 * Cyberpunk): fake room interiors behind glass without geometry. Per
 * fragment: the tangent-space view ray is intersected with the unit room box
 * the UV cell defines; the hit face + depth pick colour — either a supplied
 * room cubemap or the procedural room shading (floor/ceiling/walls tinted +
 * distance-dimmed) when none is given.
 *
 * UV convention: the facade's UV tiles `roomsX × roomsY` rooms; each cell is
 * one room, `roomDepth` deep in UV-cell units.
 */

import * as THREE from 'three/webgpu';
import {
  Fn, uniform, float, vec2, vec3, vec4, uv, fract, floor, min, mix,
  normalize, positionViewDirection, normalView, tangentView, saturate, texture,
} from 'three/tsl';

export interface InteriorMappingConfig {
  /** Rooms across the facade (default 4). */
  roomsX?: number;
  /** Rooms up the facade (default 4). */
  roomsY?: number;
  /** Room depth relative to a room cell (default 1). */
  roomDepth?: number;
  /** Optional room texture atlas (flat back-wall image per room). */
  roomTexture?: THREE.Texture;
  /** Warm interior light tint (default 1.0, 0.85, 0.6). */
  lightColor?: [number, number, number];
  /** Fraction of rooms that read as lit (default 0.65). */
  litRatio?: number;
  /** Glass tint over everything (default subtle blue). */
  glassColor?: [number, number, number];
  /** Glass reflectivity blend 0..1 (default 0.15). */
  glassReflectivity?: number;
}

export interface InteriorMappingUniforms {
  roomDepth: { value: number };
  litRatio: { value: number };
  glassReflectivity: { value: number };
  /**
   * The rooms' own LIGHT (lighting agent, 2026-09-25 — Atlas realism §10 R9):
   * the room colour is ALSO emitted, × this. 0 (the default) = the old,
   * albedo-only look. A caller on the EV scale drives it with
   * `2^EV_rel × exposureRef / exposure` so a lit window reads the same under
   * any exposure.
   */
  lightIntensity: { value: number };
}

/**
 * CPU mirror of the room-box ray walk (exported for tests): given a
 * tangent-space ray from the window plane (z=0, interior toward −z), return
 * which axis the ray exits first and at what parametric distance.
 * Room bounds: x,y ∈ [0,1] cell, z ∈ [−depth, 0].
 */
export function interiorRayCpu(
  enterX: number, enterY: number,
  dirX: number, dirY: number, dirZ: number,
  depth: number,
): { axis: 'x' | 'y' | 'z'; t: number } {
  // dirZ must look INTO the room (negative z); degenerate rays exit at z.
  const dz = Math.min(dirZ, -1e-6);
  const tz = -depth / dz;
  const tx = dirX > 0 ? (1 - enterX) / dirX : (dirX < 0 ? -enterX / dirX : Infinity);
  const ty = dirY > 0 ? (1 - enterY) / dirY : (dirY < 0 ? -enterY / dirY : Infinity);
  const t = Math.min(tz, tx, ty);
  const axis = t === tz ? 'z' : (t === tx ? 'x' : 'y');
  return { axis, t };
}

/** Deterministic per-room hash (exported for tests). */
export function roomHashCpu(cellX: number, cellY: number): number {
  const s = Math.sin(cellX * 127.1 + cellY * 311.7) * 43758.5453;
  return s - Math.floor(s);
}

export function createInteriorMappingMaterial(config?: InteriorMappingConfig): {
  material: THREE.NodeMaterial; uniforms: InteriorMappingUniforms;
} {
  const roomsX = config?.roomsX ?? 4;
  const roomsY = config?.roomsY ?? 4;
  const uDepth = uniform(config?.roomDepth ?? 1);
  const uLitRatio = uniform(config?.litRatio ?? 0.65);
  const uGlassRefl = uniform(config?.glassReflectivity ?? 0.15);
  const uLightIntensity = uniform(0);
  const lc = config?.lightColor ?? [1.0, 0.85, 0.6];
  const gc = config?.glassColor ?? [0.55, 0.65, 0.75];

  const material = new THREE.MeshStandardNodeMaterial();
  material.roughness = 0.1;
  material.metalness = 0.0;

  const interiorNode = Fn(() => {
    const cellUv = uv().mul(vec2(roomsX, roomsY));
    const cell = floor(cellUv);
    const localUv = fract(cellUv);

    // Tangent-space view ray: project the view direction onto the surface
    // frame (t, b, n). Looking INTO the surface means −(V·N).
    const t = normalize(tangentView.xyz);
    const n = normalize(normalView);
    const b = n.cross(t);
    const v = positionViewDirection;
    const dir = vec3(v.dot(t).negate(), v.dot(b).negate(), v.dot(n).negate());
    const dz = min(dir.z, float(-1e-4));

    // Exit distances to the three room planes.
    const tz = uDepth.negate().div(dz);
    const tx = dir.x.greaterThan(0).select(
      float(1).sub(localUv.x).div(dir.x),
      dir.x.lessThan(0).select(localUv.x.negate().div(dir.x), float(1e5)),
    );
    const ty = dir.y.greaterThan(0).select(
      float(1).sub(localUv.y).div(dir.y),
      dir.y.lessThan(0).select(localUv.y.negate().div(dir.y), float(1e5)),
    );
    const tHit = min(tz, min(tx, ty));

    // Per-room deterministic character: lit? warm/cool?
    const hash = fract(cell.x.mul(127.1).add(cell.y.mul(311.7)).sin().mul(43758.5453));
    const lit = hash.lessThan(uLitRatio).select(float(1), float(0.12));

    // Face shading: back wall brightest, floors dark, walls mid.
    const isBack = tHit.equal(tz).select(float(1), float(0));
    const isSide = tHit.equal(tx).select(float(1), float(0));
    const hitY = localUv.y.add(dir.y.mul(tHit));

    // Room subclasses vary (TextureNode / MathNode) — hold as unknown and
    // cast at use (the TSL fluent API is shared).
    let roomCol: unknown;
    if (config?.roomTexture) {
      const hitX = localUv.x.add(dir.x.mul(tHit));
      roomCol = vec3(texture(config.roomTexture, vec2(hitX, hitY)).rgb);
    } else {
      const backCol = vec3(lc[0], lc[1], lc[2]).mul(0.9);
      const sideCol = vec3(lc[0], lc[1], lc[2]).mul(0.45);
      const floorCeil = mix(vec3(0.15, 0.13, 0.12), vec3(lc[0], lc[1], lc[2]).mul(0.3), hitY);
      roomCol = mix(floorCeil, mix(sideCol, backCol, isBack), saturate(isBack.add(isSide)));
    }

    // Depth dimming.
    const dim = float(1).div(float(1).add(tHit.mul(0.6)));
    return (roomCol as ReturnType<typeof vec3>).mul(lit).mul(dim);
  })();
  // Glass over the room; the lit room also EMITS (× lightIntensity, 0 by default — see the uniform).
  const glass = vec3(gc[0], gc[1], gc[2]);
  material.colorNode = vec4(mix(interiorNode as never, glass, uGlassRefl), 1);
  material.emissiveNode = vec3(interiorNode as never).mul(float(1).sub(uGlassRefl)).mul(uLightIntensity);

  return {
    material: material as THREE.NodeMaterial,
    uniforms: {
      roomDepth: uDepth as unknown as { value: number },
      litRatio: uLitRatio as unknown as { value: number },
      glassReflectivity: uGlassRefl as unknown as { value: number },
      lightIntensity: uLightIntensity as unknown as { value: number },
    } as InteriorMappingUniforms,
  };
}
